import * as i0 from '@angular/core';
import { inject, ElementRef, Component, ViewEncapsulation, ChangeDetectionStrategy, HostBinding, ContentChildren, Input, NgModule } from '@angular/core';
import { Validators, ReactiveFormsModule } from '@angular/forms';
import { MatChipRow, MatChipsModule } from '@angular/material/chips';
import { MatFormField, MatFormFieldControl, MatFormFieldModule } from '@angular/material/form-field';
import { DropzoneComponent, coerceBoolean, DropzoneCdkModule } from '@ngx-dropzone/cdk';
import { Subject, merge, tap, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

class MatDropzone extends DropzoneComponent {
    constructor() {
        super(...arguments);
        this._elementRef = inject(ElementRef);
        this._formField = inject(MatFormField);
        this.id = `mat-dropzone-component-${MatDropzone.nextId++}`;
        this.controlType = 'ngx-mat-dropzone';
        // The file input is never autofilled
        this.autofilled = false;
        this.stateChanges = new Subject();
        this.ngControl = this.fileInputDirective?.ngControl ?? null;
        this._placeholder = '';
        this._required = false;
    }
    static { this.nextId = 0; }
    get placeholder() {
        return this._placeholder;
    }
    set placeholder(value) {
        this._placeholder = value;
        this.stateChanges.next();
    }
    get required() {
        const controlRequired = this.ngControl?.control?.hasValidator(Validators.required);
        return this._required || controlRequired || false;
    }
    set required(value) {
        this._required = coerceBoolean(value);
        this.stateChanges.next();
    }
    get filled() {
        return this._formField?.appearance === 'fill';
    }
    get empty() {
        return this.fileInputDirective?.empty || true;
    }
    get shouldLabelFloat() {
        return this._matChips.length > 0;
    }
    get ariaInvalid() {
        return this.empty && this.required ? null : this.errorState;
    }
    ngAfterContentInit() {
        super.ngAfterContentInit();
        // Forward the stateChanges from the fileInputDirective to the MatFormFieldControl
        const stateEvents = [this.dragover$];
        if (this.fileInputDirective)
            stateEvents.push(this.fileInputDirective.stateChanges);
        merge(...stateEvents)
            .pipe(tap(() => this.stateChanges.next()), takeUntil(this._destroy$))
            .subscribe();
    }
    ngAfterContentChecked() {
        // Set the dropzone to cover the whole form field
        const formField = this._formField?._elementRef?.nativeElement;
        this._elementRef.nativeElement.style.width = `${formField?.offsetWidth ?? 0}px`;
        // Set the dropzone height depending on the form field appearance
        this._elementRef.nativeElement.style.marginTop = `-${this.filled ? 24 : 16}px`;
        this._elementRef.nativeElement.style.marginBottom = `-${this.filled ? 8 : 16}px`;
    }
    onContainerClick() {
        this.openFilePicker();
    }
    setDescribedByIds(ids) {
        if (ids.length) {
            this._elementRef.nativeElement.setAttribute('aria-describedby', ids.join(' '));
        }
        else {
            this._elementRef.nativeElement.removeAttribute('aria-describedby');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: MatDropzone, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.2", type: MatDropzone, selector: "ngx-mat-dropzone", inputs: { userAriaDescribedBy: ["aria-describedby", "userAriaDescribedBy"], placeholder: "placeholder", required: "required" }, host: { properties: { "id": "this.id", "attr.aria-required": "this.required", "attr.aria-invalid": "this.ariaInvalid" } }, providers: [
            {
                provide: MatFormFieldControl,
                useExisting: MatDropzone,
            },
        ], queries: [{ propertyName: "_matChips", predicate: MatChipRow }], exportAs: ["matDropzone"], usesInheritance: true, ngImport: i0, template: `
    <ng-content select="[fileInput]"></ng-content>
    <div class="mat-chip-grid" [class.filled-and-float]="filled && shouldLabelFloat">
      <ng-content select="mat-chip-row"></ng-content>
    </div>
  `, isInline: true, styles: ["ngx-mat-dropzone{display:flex;position:relative;z-index:10;min-height:56px;margin:0 -16px;color:currentcolor;outline:none;cursor:pointer;font:inherit}.mat-chip-grid{display:flex;flex-flow:wrap;margin:8px}.mat-chip-grid.filled-and-float{padding-top:16px}.mat-chip-grid>.mdc-evolution-chip{margin:4px 0 4px 8px}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: MatDropzone, decorators: [{
            type: Component,
            args: [{ selector: 'ngx-mat-dropzone', exportAs: 'matDropzone', template: `
    <ng-content select="[fileInput]"></ng-content>
    <div class="mat-chip-grid" [class.filled-and-float]="filled && shouldLabelFloat">
      <ng-content select="mat-chip-row"></ng-content>
    </div>
  `, encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            provide: MatFormFieldControl,
                            useExisting: MatDropzone,
                        },
                    ], styles: ["ngx-mat-dropzone{display:flex;position:relative;z-index:10;min-height:56px;margin:0 -16px;color:currentcolor;outline:none;cursor:pointer;font:inherit}.mat-chip-grid{display:flex;flex-flow:wrap;margin:8px}.mat-chip-grid.filled-and-float{padding-top:16px}.mat-chip-grid>.mdc-evolution-chip{margin:4px 0 4px 8px}\n"] }]
        }], propDecorators: { id: [{
                type: HostBinding
            }], _matChips: [{
                type: ContentChildren,
                args: [MatChipRow]
            }], userAriaDescribedBy: [{
                type: Input,
                args: ['aria-describedby']
            }], placeholder: [{
                type: Input
            }], required: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.aria-required']
            }], ariaInvalid: [{
                type: HostBinding,
                args: ['attr.aria-invalid']
            }] } });

class DropzoneMaterialModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: DropzoneMaterialModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.2", ngImport: i0, type: DropzoneMaterialModule, declarations: [MatDropzone], imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatChipsModule, MatIconModule, DropzoneCdkModule], exports: [MatDropzone] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: DropzoneMaterialModule, imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatChipsModule, MatIconModule, DropzoneCdkModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: DropzoneMaterialModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MatDropzone],
                    imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatChipsModule, MatIconModule, DropzoneCdkModule],
                    exports: [MatDropzone],
                }]
        }] });

/*
 * Public API Surface of material
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DropzoneMaterialModule, MatDropzone };
